/**
 * Shiki code highlighting — lazy, dual-theme (github-light + github-dark).
 *
 * Shiki is heavy (the engine + grammars + themes weigh hundreds of KB), so
 * we never load it at startup. `primeHighlighter()` is the lazy-import gate
 * — the first call kicks off the dynamic import + createHighlighter, every
 * subsequent call awaits the same cached Promise. A document with zero code
 * fences pays zero Shiki bytes.
 *
 * Sixteen languages are preloaded (the spec's curated list — covers ~95%
 * of pasted READMEs without runtime grammar fetches): js, ts, jsx, tsx,
 * json, html, css, svelte, python, bash, sh, sql, yaml, toml, md, diff.
 * Anything else falls back to a plain escaped <pre><code class="code-fallback">.
 *
 * Dual-theme output is produced via `themes: { light, dark }, defaultColor: false`,
 * which writes both `--shiki-light` and `--shiki-dark` custom properties on
 * every span. The dark-mode swap lives in `src/theme/shiki.css` — under
 * `[data-mode="dark"] .shiki` we promote the dark vars to actual color +
 * background. No JS theme switching needed.
 *
 * `hydrateCode(container)` is called after the rendered HTML lands in the
 * DOM (Phase 6, Preview.svelte). It walks `pre > code[class*="language-"]`
 * blocks emitted by markdown-it's default fence rule, swaps each `<pre>`
 * for the highlighted output. Idempotent enough — we'd need to guard if
 * Phase 6 runs it twice per render, but the obvious gate is
 * `pre.classList.contains('shiki')` which the replacement sets.
 */

import type { Highlighter } from 'shiki'

// The 16 preloaded languages — keep this list in lock-step with the docs
// in CLAUDE.md / spec §14. Adding a language is one line + a build size hit.
const LANGS = [
  'js', 'ts', 'jsx', 'tsx', 'json', 'html', 'css', 'svelte', 'python',
  'bash', 'sh', 'sql', 'yaml', 'toml', 'md', 'diff',
] as const

const THEMES = ['github-light', 'github-dark'] as const

let highlighterPromise: Promise<Highlighter> | null = null

/**
 * Lazy-load the Shiki highlighter. Cached Promise so concurrent renders
 * don't trigger two engine boots. Tests can `await primeHighlighter()`
 * explicitly to keep their assertions synchronous after the boot lands.
 */
export function primeHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import('shiki').then((mod) =>
      mod.createHighlighter({
        langs: [...LANGS],
        themes: [...THEMES],
      }),
    )
  }
  return highlighterPromise
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function fallback(code: string): string {
  return `<pre class="code-fallback"><code>${escapeHtml(code)}</code></pre>`
}

/**
 * Highlight a single code block. Unknown languages fall back to a plain
 * escaped pre/code so unknown fences (or `text` / `plaintext` / random
 * lang tags) still produce safe, readable output.
 */
export async function highlightCode(
  code: string,
  lang: string,
): Promise<string> {
  const hl = await primeHighlighter()
  const loaded = hl.getLoadedLanguages()
  const effective = loaded.includes(lang as never) ? lang : null
  if (!effective) {
    return fallback(code)
  }
  return hl.codeToHtml(code, {
    lang: effective,
    themes: { light: 'github-light', dark: 'github-dark' },
    // defaultColor:false emits CSS custom props on every span instead of
    // baking in one theme. theme/shiki.css promotes --shiki-dark to actual
    // color under [data-mode="dark"], so the swap is pure CSS.
    defaultColor: false,
  })
}

const LANG_RE = /language-(\S+)/

/**
 * Walk a mounted DOM container and replace every default markdown-it code
 * block (`<pre><code class="language-X">…</code></pre>`) with the highlighted
 * Shiki output. Unknown languages get the escape-only fallback so they
 * still render in monospace.
 *
 * Idempotent guard: skip blocks where the parent `<pre>` already has the
 * `shiki` class (we already highlighted this one).
 */
export async function hydrateCode(container: HTMLElement): Promise<void> {
  const blocks = container.querySelectorAll<HTMLElement>(
    'pre > code[class*="language-"]',
  )
  if (blocks.length === 0) return
  // Boot the engine once for the whole batch.
  await primeHighlighter()
  for (const codeEl of blocks) {
    const pre = codeEl.parentElement
    if (!pre || pre.tagName !== 'PRE') continue
    if (pre.classList.contains('shiki')) continue
    const langMatch = codeEl.className.match(LANG_RE)
    const lang = langMatch?.[1] ?? ''
    const source = codeEl.textContent ?? ''
    const html = await highlightCode(source, lang)
    // Race guard: preview may have re-rendered while we awaited highlightCode,
    // which detaches the pre from the DOM. Setting outerHTML on a detached
    // node throws "This element has no parent node". Skip — the new render's
    // pass through hydrateCode will pick this block up afresh.
    if (!pre.isConnected) continue
    pre.outerHTML = html
  }
}
